import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import ConfirmationModal from './components/common/ConfirmationModal';
import { COURSES, normalizeCourses } from './utils/constants';

function MentorManagement() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [courses, setCourses] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ type: 'idle', message: '' });
  const [mentors, setMentors] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(true);
  const [rosterError, setRosterError] = useState('');
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, mentorId: null, courseToRemove: null });
  const [transferModal, setTransferModal] = useState({ isOpen: false, fromMentorId: null });
  const [transferring, setTransferring] = useState(false);
  const [resetModal, setResetModal] = useState({ isOpen: false, mentorId: null, mentorName: '' });
  const [resettingPassword, setResettingPassword] = useState(false);

  const fetchMentorRoster = useCallback(async () => {
    setRosterLoading(true);
    setRosterError('');
    try {
      const [mentorRes, studentRes, callsRes] = await Promise.all([
        supabase
          .from('mentors')
          .select('id, full_name, email, course, created_at')
          .order('created_at', { ascending: false }),
        supabase
          .from('students')
          .select('mentor_id'),
        supabase
          .from('calls')
          .select('mentor_id, call_status, created_at')
      ]);

      if (mentorRes.error) throw mentorRes.error;

      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      const weekAgoStr = weekAgo.toISOString();

      const studentCounts = (studentRes.data ?? []).reduce((acc, row) => {
        if (!row.mentor_id) return acc;
        acc[row.mentor_id] = (acc[row.mentor_id] || 0) + 1;
        return acc;
      }, {});

      const mentorCalls = {};
      const mentorCallsWeek = {};
      const mentorLastActive = {};
      (callsRes.data ?? []).forEach(call => {
        if (!call.mentor_id) return;
        mentorCalls[call.mentor_id] = (mentorCalls[call.mentor_id] || 0) + 1;
        if (call.created_at >= weekAgoStr) {
          mentorCallsWeek[call.mentor_id] = (mentorCallsWeek[call.mentor_id] || 0) + 1;
        }
        const callDate = new Date(call.created_at);
        if (!mentorLastActive[call.mentor_id] || callDate > mentorLastActive[call.mentor_id]) {
          mentorLastActive[call.mentor_id] = callDate;
        }
      });

      const enriched = (mentorRes.data ?? []).map((mentor) => {
        const totalCalls = mentorCalls[mentor.id] || 0;
        const callsThisWeek = mentorCallsWeek[mentor.id] || 0;
        const lastActive = mentorLastActive[mentor.id];
        const isActive = lastActive && (new Date() - lastActive) / (1000 * 60 * 60 * 24) <= 7;
        
        return {
          ...mentor,
          studentCount: studentCounts[mentor.id] || 0,
          totalCalls,
          callsThisWeek,
          isActive
        };
      });

      setMentors(enriched);
    } catch (error) {
      console.error('Failed to fetch mentor roster', error);
      setRosterError('Unable to load mentor roster. Please try again.');
    } finally {
      setRosterLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMentorRoster();
  }, [fetchMentorRoster]);

  const handleRemoveCourseClick = (mentorId, courseToRemove) => {
    setConfirmModal({ isOpen: true, mentorId, courseToRemove });
  };

  const handleConfirmRemoveCourse = async () => {
    const { mentorId, courseToRemove } = confirmModal;
    try {
      const mentor = mentors.find(m => m.id === mentorId);
      if (!mentor) throw new Error('Mentor not found');

      const currentCourses = normalizeCourses(mentor.course);
      const updatedCourses = currentCourses.filter(c => c !== courseToRemove);

      const { error } = await supabase
        .from('mentors')
        .update({ course: updatedCourses })
        .eq('id', mentorId);

      if (error) throw error;

      fetchMentorRoster();
    } catch (error) {
      console.error('Error removing course from mentor', error);
      setRosterError('Failed to remove course from mentor. Please try again.');
    }
  };

  const handleTransferClick = (mentorId) => {
    setTransferModal({ isOpen: true, fromMentorId: mentorId });
  };

  const handleTransferSubmit = async (targetMentorId, courseToTransfer) => {
    if (!targetMentorId || !transferModal.fromMentorId) return;

    setTransferring(true);
    try {
      let query = supabase
        .from('students')
        .update({ mentor_id: targetMentorId })
        .eq('mentor_id', transferModal.fromMentorId);

      if (courseToTransfer) {
        query = query.eq('course', courseToTransfer);
      }

      const { error } = await query;
      if (error) throw error;

      setTransferModal({ isOpen: false, fromMentorId: null });
      fetchMentorRoster();
      setFeedback({ type: 'success', message: 'Students transferred successfully.' });
    } catch (error) {
      console.error('Error transferring students', error);
      setRosterError('Failed to transfer students. Please try again.');
    } finally {
      setTransferring(false);
    }
  };

  const [addCourseModal, setAddCourseModal] = useState({ isOpen: false, mentorId: null, currentCourses: [] });
  const [addingCourse, setAddingCourse] = useState(false);

  const handleResetPasswordClick = (mentor) => {
    setResetModal({ isOpen: true, mentorId: mentor.id, mentorName: mentor.full_name || mentor.email });
  };

  const handleResetPasswordSubmit = async (newPassword) => {
    if (!newPassword || !resetModal.mentorId) return;

    setResettingPassword(true);
    try {
      const { error } = await supabase.functions.invoke('resetMentorPassword', {
        body: { mentorId: resetModal.mentorId, newPassword }
      });

      if (error) throw error;

      setFeedback({ type: 'success', message: `Password for ${resetModal.mentorName} reset successfully.` });
    } catch (error) {
      console.error('Error resetting password', error);
      setFeedback({ type: 'error', message: error?.message || 'Failed to reset password. Please try again.' });
    } finally {
      setResettingPassword(false);
      setResetModal({ isOpen: false, mentorId: null, mentorName: '' });
    }
  };

  const handleAddCourseClick = (mentor) => {
    const currentCourses = normalizeCourses(mentor.course);
    setAddCourseModal({ isOpen: true, mentorId: mentor.id, currentCourses });
  };

  const handleAddCourseSubmit = async (newCourses) => {
    if (newCourses.length === 0) return;
    setAddingCourse(true);
    try {
      const { mentorId, currentCourses } = addCourseModal;
      const updatedCourses = [...currentCourses, ...newCourses];

      const { error } = await supabase
        .from('mentors')
        .update({ course: updatedCourses })
        .eq('id', mentorId);

      if (error) throw error;

      setAddCourseModal({ isOpen: false, mentorId: null, currentCourses: [] });
      fetchMentorRoster();
    } catch (error) {
      console.error('Error adding courses', error);
      setRosterError('Failed to add courses. Please try again.');
    } finally {
      setAddingCourse(false);
    }
  };

  const handleCreateMentor = async () => {
    if (!name || !email || !password || courses.length === 0) {
      setFeedback({ type: 'error', message: 'Please fill out all fields and select at least one course.' });
      return;
    }
    setSubmitting(true);
    setFeedback({ type: 'idle', message: '' });
    try {
      const { error } = await supabase.functions.invoke('createMentor', {
        body: { name, email, password, course: courses }
      });
      if (error) throw error;

      setFeedback({ type: 'success', message: 'Mentor created successfully! A welcome email has been sent.' });
      setName('');
      setEmail('');
      setPassword('');
      setCourses([]);
      fetchMentorRoster();
    } catch (err) {
      const message = err?.message || 'Error creating mentor. Please try again later.';
      setFeedback({ type: 'error', message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-8">
      <header className="space-y-2 animate-fade-in">
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 dark:text-white">Mentor management</h1>
        <p className="text-slate-600 dark:text-slate-400">Create mentor accounts and manage onboarding from a single dashboard.</p>
      </header>

      <section className="card animate-fade-in">
        <div className="flex items-start justify-between gap-6 flex-wrap mb-6">
          <div className="space-y-1">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Invite a new mentor</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">We will automatically create the account and send onboarding instructions.</p>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <label className="space-y-1">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Full name</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Alex Johnson"
              className="input"
            />
          </label>
          <label className="space-y-1">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Email address</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="mentor@example.com"
              className="input"
            />
          </label>
          <label className="md:col-span-2 space-y-1">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Temporary password</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Provide a secure temporary password"
              className="input"
            />
            <p className="text-xs text-slate-500 dark:text-slate-500">Mentors will be prompted to update their password on first login.</p>
          </label>
          <label className="md:col-span-2 space-y-2">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Courses</span>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {COURSES.map((option) => (
                <label key={option} className="flex items-center space-x-2 p-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 cursor-pointer hover:bg-white dark:hover:bg-slate-800 transition">
                  <input
                    type="checkbox"
                    value={option}
                    checked={courses.includes(option)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setCourses([...courses, option]);
                      } else {
                        setCourses(courses.filter((c) => c !== option));
                      }
                    }}
                    className="rounded border-slate-300 text-brand focus:ring-brand"
                  />
                  <span className="text-sm text-slate-700 dark:text-slate-300">{option}</span>
                </label>
              ))}
            </div>
          </label>
        </div>

        {feedback.message && (
          <div className={`mt-6 rounded-xl border px-4 py-3 text-sm font-medium ${
            feedback.type === 'success'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-400/30 dark:bg-emerald-500/10 dark:text-emerald-200'
              : 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-400/30 dark:bg-rose-500/10 dark:text-rose-200'
          }`}>
            {feedback.message}
          </div>
        )}

        <div className="mt-6 flex items-center justify-end">
          <button
            type="button"
            onClick={handleCreateMentor}
            disabled={submitting}
            className="btn btn-primary"
          >
            {submitting ? 'Sending invite…' : 'Create mentor'}
          </button>
        </div>
      </section>

      <section className="card animate-fade-in">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Mentor roster</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">Review active mentors and see how many students are assigned to each.</p>
          </div>
          <button
            type="button"
            onClick={fetchMentorRoster}
            className="btn btn-secondary"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh roster
          </button>
        </div>

        {rosterError && (
          <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-400/40 dark:bg-rose-500/10 dark:text-rose-200">
            {rosterError}
          </div>
        )}

        <div>
          {rosterLoading ? (
            <div className="flex h-32 items-center justify-center text-sm text-slate-500 dark:text-slate-400">Loading mentor roster…</div>
          ) : mentors.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">👤</div>
              <h3 className="empty-state-title">No mentors found</h3>
              <p className="empty-state-description">Invite your first mentor to see them listed here.</p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 stagger-children">
              {mentors.map((mentor) => (
                <article
                  key={mentor.id}
                  onClick={() => navigate(`/students?mentor=${mentor.id}`)}
                  className="card cursor-pointer hover:border-brand/40 hover:shadow-md transition-all group"
                >
                  <header className="space-y-1">
                    <div className="flex items-center justify-between">
                      <h3 className="text-base font-semibold text-slate-900 dark:text-white group-hover:text-brand transition-colors">
                        {mentor.full_name || 'Unnamed mentor'}
                      </h3>
                      <div className="activity-indicator">
                        <span className={`activity-dot ${mentor.isActive ? 'active' : 'inactive'}`}></span>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{mentor.email}</p>
                    
                    {(normalizeCourses(mentor.course)).length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {(normalizeCourses(mentor.course)).map((c, idx) => (
                          <div key={idx} className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                            <span>{c}</span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveCourseClick(mentor.id, c);
                              }}
                              className="ml-1 text-slate-400 hover:text-rose-500 dark:text-slate-500 dark:hover:text-rose-400"
                              title="Remove course assignment"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddCourseClick(mentor);
                      }}
                      className="mt-2 text-xs font-medium text-brand hover:text-brand-dark dark:text-brand-light dark:hover:text-brand flex items-center gap-1"
                    >
                      <span>+ Add Course</span>
                    </button>
                  </header>

                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTransferClick(mentor.id);
                      }}
                      className="text-xs font-medium text-brand hover:text-brand-dark dark:text-brand-light dark:hover:text-brand px-2 py-1 rounded bg-brand/5 hover:bg-brand/10 dark:bg-brand/10 dark:hover:bg-brand/20 transition-colors"
                    >
                      Transfer Students
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleResetPasswordClick(mentor);
                      }}
                      className="text-xs font-medium text-amber-600 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300 px-2 py-1 rounded bg-amber-50 hover:bg-amber-100 dark:bg-amber-500/10 dark:hover:bg-amber-500/20 transition-colors"
                    >
                      Reset Password
                    </button>
                  </div>

                  <dl className="mt-4 grid grid-cols-3 gap-4 text-sm border-t border-slate-100 dark:border-slate-800 pt-4">
                    <div className="text-center">
                      <dt className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">Students</dt>
                      <dd className="text-lg font-semibold text-brand">{mentor.studentCount}</dd>
                    </div>
                    <div className="text-center">
                      <dt className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">Calls (Week)</dt>
                      <dd className="text-lg font-semibold text-slate-700 dark:text-slate-200">{mentor.callsThisWeek}</dd>
                    </div>
                    <div className="text-center">
                      <dt className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">Total Calls</dt>
                      <dd className="text-lg font-semibold text-slate-700 dark:text-slate-200">{mentor.totalCalls}</dd>
                    </div>
                  </dl>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={handleConfirmRemoveCourse}
        title="Remove Course Assignment"
        message="Are you sure you want to remove this course assignment? This action cannot be undone."
        confirmText="Remove"
        isDestructive={true}
      />

      <TransferModal
        isOpen={transferModal.isOpen}
        onClose={() => setTransferModal({ ...transferModal, isOpen: false })}
        onConfirm={handleTransferSubmit}
        mentors={mentors}
        fromMentorId={transferModal.fromMentorId}
        loading={transferring}
      />

      <AddCourseModal
        isOpen={addCourseModal.isOpen}
        onClose={() => setAddCourseModal({ ...addCourseModal, isOpen: false })}
        onConfirm={handleAddCourseSubmit}
        currentCourses={addCourseModal.currentCourses}
        loading={addingCourse}
      />

      <ResetPasswordModal
        isOpen={resetModal.isOpen}
        onClose={() => setResetModal({ ...resetModal, isOpen: false })}
        onConfirm={handleResetPasswordSubmit}
        mentorName={resetModal.mentorName}
        loading={resettingPassword}
      />
    </div>
  );
}

const ResetPasswordModal = ({ isOpen, onClose, onConfirm, mentorName, loading }) => {
  const [newPassword, setNewPassword] = React.useState('');

  React.useEffect(() => {
    setNewPassword('');
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm transition-opacity">
      <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-slate-900/5 dark:bg-slate-900 dark:ring-white/10 animate-scale-in">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Reset Password</h3>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            Set a new temporary password for <span className="font-semibold text-slate-700 dark:text-slate-300">{mentorName}</span>.
          </p>

          <div className="mt-4 space-y-4">
            <div>
              <label htmlFor="new-password" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">New Password</label>
              <input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                className="input"
              />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 bg-slate-50 px-6 py-4 dark:bg-slate-800/50">
          <button type="button" onClick={onClose} disabled={loading} className="btn btn-secondary">
            Cancel
          </button>
          <button type="button" onClick={() => onConfirm(newPassword)} disabled={!newPassword || loading} className="btn bg-amber-600 text-white hover:bg-amber-700">
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </div>
      </div>
    </div>
  );
};

const TransferModal = ({ isOpen, onClose, onConfirm, mentors, fromMentorId, loading }) => {
  const [targetId, setTargetId] = React.useState('');
  const [selectedCourse, setSelectedCourse] = React.useState('');

  React.useEffect(() => {
    setTargetId('');
    setSelectedCourse('');
  }, [isOpen, fromMentorId]);

  if (!isOpen) return null;

  const sourceMentor = mentors.find(m => m.id === fromMentorId);
  const eligibleTargets = mentors.filter(m => m.id !== fromMentorId);

  const sourceCourses = sourceMentor
    ? normalizeCourses(sourceMentor.course)
    : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm transition-opacity">
      <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-slate-900/5 dark:bg-slate-900 dark:ring-white/10 animate-scale-in">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Transfer Students</h3>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            Move students from <span className="font-semibold text-slate-700 dark:text-slate-300">{sourceMentor?.full_name || 'this mentor'}</span> to another mentor.
          </p>

          <div className="mt-4 space-y-4">
            <div>
              <label htmlFor="transfer-course-select" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Select Course (Optional)</label>
              <select
                id="transfer-course-select"
                value={selectedCourse}
                onChange={(e) => setSelectedCourse(e.target.value)}
                className="input select"
              >
                <option value="">All Courses (Transfer Everyone)</option>
                {sourceCourses.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div>
              <label htmlFor="transfer-mentor-select" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Select New Mentor</label>
              <select
                id="transfer-mentor-select"
                value={targetId}
                onChange={(e) => setTargetId(e.target.value)}
                className="input select"
              >
                <option value="">Choose a mentor...</option>
                {eligibleTargets.map(m => (
                  <option key={m.id} value={m.id}>{m.full_name || m.email} ({m.studentCount} students)</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 bg-slate-50 px-6 py-4 dark:bg-slate-800/50">
          <button type="button" onClick={onClose} disabled={loading} className="btn btn-secondary">
            Cancel
          </button>
          <button type="button" onClick={() => onConfirm(targetId, selectedCourse)} disabled={!targetId || loading} className="btn btn-primary">
            {loading ? 'Transferring...' : 'Transfer Students'}
          </button>
        </div>
      </div>
    </div>
  );
};

const AddCourseModal = ({ isOpen, onClose, onConfirm, currentCourses, loading }) => {
  const [selectedCourses, setSelectedCourses] = React.useState([]);
  const allCourses = COURSES;
  const availableCourses = allCourses.filter(c => !currentCourses.includes(c));

  React.useEffect(() => {
    setSelectedCourses([]);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm transition-opacity">
      <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-slate-900/5 dark:bg-slate-900 dark:ring-white/10 animate-scale-in">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Add Courses</h3>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">Select courses to add to this mentor.</p>

          <div className="mt-4 space-y-2">
            {availableCourses.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">All courses already assigned.</p>
            ) : (
              availableCourses.map((course) => (
                <label key={course} className="flex items-center space-x-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedCourses.includes(course)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedCourses(prev => [...prev, course]);
                      } else {
                        setSelectedCourses(prev => prev.filter(c => c !== course));
                      }
                    }}
                    className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand"
                  />
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{course}</span>
                </label>
              ))
            )}
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 bg-slate-50 px-6 py-4 dark:bg-slate-800/50">
          <button type="button" onClick={onClose} disabled={loading} className="btn btn-secondary">
            Cancel
          </button>
          <button type="button" onClick={() => onConfirm(selectedCourses)} disabled={selectedCourses.length === 0 || loading} className="btn btn-primary">
            {loading ? 'Adding...' : 'Add Courses'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MentorManagement;